import { refContainsTarget } from "@prismicio/editor-support/React"
import { useFetcherData } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOptions,
  DialogOptionsItem,
  type MediaAssetType,
  useAlert,
} from "@prismicio/editor-ui"
import { useEffect, useState } from "react"

import { MediaLibrary } from "../../../core/MediaLibrary"
import {
  clearMediaCacheAndRevalidateFirstPage,
  type ExternalImage,
  type MediaAssetOrExternalImage,
  searchMedia,
} from "../../../core/MediaLibrary/hooks/mediaLibraryData"
import { useMediaLibraryAnalyticsMediaAddedToPage } from "../../../core/MediaLibrary/hooks/useMediaLibraryAnalytics"
import { UnsplashLibrary } from "../../../core/UnsplashLibrary/UnsplashLibrary"
import { useEditorConfig } from "../../../EditorConfig"

interface MediaDialogProps {
  assetType: MediaAssetType
  open: boolean
  onOpenChange: (open: boolean) => void
  onMediaSelected: (media: MediaAssetOrExternalImage) => void
}

export function MediaDialog(props: MediaDialogProps) {
  const { open, onOpenChange: setIsOpen, onMediaSelected, assetType } = props

  const config = useEditorConfig()

  const mediaLibraryAvailable = config.mediaLibrary

  const [option, setOption] = useState<"media" | "unsplash">(() =>
    mediaLibraryAvailable ? "media" : "unsplash",
  )

  const [selectedMediaAssetIds, setSelectedMediaAssetIds] = useState<
    Set<string>
  >(new Set())

  const [selectedExternalImages, setSelectedExternalImages] = useState<
    ExternalImage[]
  >([])

  const selectedMediaCount =
    selectedMediaAssetIds.size + selectedExternalImages.length

  const mediaCache = useFetcherData(searchMedia)

  useEffect(() => {
    if (!open || !mediaLibraryAvailable) return
    clearMediaCacheAndRevalidateFirstPage({ config, assetType })
  }, [open, config, assetType, mediaLibraryAvailable])

  function onOptionChange(option: string) {
    if (option !== "media" && option !== "unsplash")
      throw new Error(`Invalid option ${option}.`)
    setOption(option)
  }

  function onOpenChange(open: boolean) {
    if (!open) setSelectedMediaAssetIds(new Set())
    setIsOpen(open)
  }

  const onAnalyticsEventMediaAddedToPage =
    useMediaLibraryAnalyticsMediaAddedToPage({
      assetType,
      mediaDialogOpen: open,
    })

  function onAddToPage() {
    switch (option) {
      case "unsplash": {
        const mediaItem = selectedExternalImages[0]
        if (!mediaItem) return
        onMediaSelected(mediaItem)
        config.onAnalyticsEvent?.("Unsplash Image Added")
        break
      }
      case "media": {
        const medias = mediaCache.flatMap((cacheEntry) => cacheEntry.items)
        const selectedMediaImages = medias.filter((media) =>
          selectedMediaAssetIds.has(media.id),
        )
        const mediaItem = selectedMediaImages[0]
        if (!mediaItem) return

        onMediaSelected(mediaItem)
        onAnalyticsEventMediaAddedToPage(mediaItem.id)
        break
      }
    }
  }

  function getActionTooltip() {
    if (selectedMediaCount === 0)
      return "Select a media item to add to the page"

    if (selectedMediaCount > 1)
      return "Only one media item can be added to the page"

    return undefined
  }

  const { containerRef } = useAlert()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size={{
        // Full width minus the 24px padding on the left and right. Max width is 2000px on large screens
        width: "min(calc(100% - 48px), 2000px)",
        // Full height minus the 15px padding on the top and bottom
        height: "calc(100% - 30px)",
      }}
      position="center"
      onPointerDownOutside={(event) => {
        if (containerRef && refContainsTarget(containerRef, event.target)) {
          event.preventDefault()
        }
      }}
      id="media"
    >
      <DialogHeader icon="image" title="Media library" />
      <DialogDescription hidden>
        Select a media item to add to the page
      </DialogDescription>

      <DialogOptions
        value={option}
        onValueChange={onOptionChange}
        padding="small"
        width="auto"
      >
        {mediaLibraryAvailable && (
          <DialogOptionsItem value="media" icon="photo" />
        )}
        <DialogOptionsItem value="unsplash" icon="unsplash" />
      </DialogOptions>

      <DialogContent>
        <Box flexDirection="column" flexGrow={1} overflow="hidden">
          <Box
            overflow="hidden"
            backgroundColor="grey2"
            padding={{ top: 16, inline: 16 }}
            flexGrow={1}
          >
            {option === "media" && (
              <MediaLibrary
                assetType={assetType}
                onSelectedChange={setSelectedMediaAssetIds}
              />
            )}
            {option === "unsplash" && (
              <UnsplashLibrary onSelectedChange={setSelectedExternalImages} />
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton
          onClick={onAddToPage}
          disabled={selectedMediaCount !== 1}
          tooltipLabel={getActionTooltip()}
        >
          Add to page
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}
